import { Rect } from '../../../platform/geom/Rect';
import { TextSettings } from '../../canvas/entities/TextConventions';
import { TextRuler } from '../../canvas/visualization/TextRuler';
import { JiraCard, JiraCardCustomInfo, JiraCardCustomsVisibleAttr } from './JiraCard';
import { JiraSummaryTextSettings } from './fields/JiraSummaryField';

const smTextSettings = TextSettings.from({
    ...TextSettings.default,
    fontSize: 11,
    textMargin: [0, 4],
});

const mdTextSettings = TextSettings.from({
    ...TextSettings.default,
    fontSize: 16,
    textMargin: 0,
});

const computeCustomsLayout = (object: JiraCard, initial: Rect) => {

    const show = JiraCardCustomsVisibleAttr.get(object);
    const customs = {} as Record<string, Rect>;

    let layoutX = initial.left;
    let layoutY = initial.top;

    for (const ci of object.customs.filter(x => show?.includes(x.key))) {
        const value = JiraCardCustomInfo.longestLabelFor(ci);
        const computedWidth = Math.max(40, 16 + (!!value ? TextRuler.measureSize(`${value}`, mdTextSettings).w : 0));
        const cappedWidth = Math.min(computedWidth, initial.width);

        if (layoutX + cappedWidth > initial.right) {
            layoutX = initial.left;
            layoutY += 24 + 10;
        }

        customs[ci.key] = new Rect(layoutX, layoutY, cappedWidth, 24);
        layoutX += cappedWidth + 10;
    }

    return customs;
};

const computeFooterLayout = (object: JiraCard, main: Rect) => {

    const footer = main.set({
        top: main.bottom,
        height: 50,
    });

    const inner = new Rect(
        footer.left + 15,
        footer.top + 10,
        footer.width - 30,
        footer.height - 20,
    );

    const issueType = inner.set({
        top: inner.top,
        left: inner.left,
        width: 30,
    });

    const key = inner.set({
        top: inner.top,
        left: issueType.right + 10,
        width: TextRuler.measureSize(object.key, mdTextSettings).w
    });

    const assignee = inner.set({
        top: inner.top,
        left: inner.right - 30,
        width: 30,
    });

    const priority = inner.set({
        top: inner.top,
        left: assignee.left - 10 - 30,
        width: 30,
    });

    const status = inner.set({
        top: inner.top,
        left: priority.left - 10 - 100,
        width: 100,
    });

    return { footer, assignee, priority, status, issueType, key };
};

export const JiraCardLayout = (object: JiraCard) => {
    const source = new Rect(0, 0, object.width, 0);

    if (!!object?.record?.issueType) {

        const inner = source.set({
            left: 15,
            right: source.right - 15,
            top: 15,
        });

        const summary = inner.set({
            width: inner.width - 30 - 5,
            height: Math.max(24, TextRuler.measureHeight(object.record.summary || '', JiraSummaryTextSettings, inner.width - 30 - 5)),
        });

        const more = summary.set({
            left: summary.right + 5,
            width: 30,
            height: 30,
        });

        const parent = inner.set({
            top: summary.bottom + 10,
            width: Math.min(
                TextRuler.measureSize((object.record.parent?.summary || 'Set Epic').toUpperCase(), smTextSettings).w + 8,
                inner.width
            ),
            height: 18,
        });

        const customs = computeCustomsLayout(object, inner.set({ top: parent.bottom + 20 }));
        const customBounds = inner.set({
            top: parent.bottom + 20,
            height: Rect.fromMany(Object.values(customs)).height,
        });

        const main = source.set({ height: customBounds.bottom + 12 });
        const { footer, ...footerItems } = computeFooterLayout(object, main);
        const base = source.set({ height: footer.bottom + 3 });
        return { base, footer, ...footerItems, customs, parent, summary, more };

    }
    else {
        const main = source.set({ height: object.height - 55 });
        const { footer, ...footerItems } = computeFooterLayout(object, main);
        const base = source.set({ height: footer.bottom + 5 });
        const customs = Rect.empty;
        const parent = Rect.empty;
        const summary = Rect.empty;
        const more = Rect.empty;
        return { base, footer, ...footerItems, customs, parent, summary, more };
    }
};

export type JiraCardLayout = ReturnType<typeof JiraCardLayout>;
